import { useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, CalendarCheck, Heart, Wine, UtensilsCrossed } from "lucide-react";
import { WEDDING, IMAGES } from "@/lib/constants";
import StickyNav from "@/components/StickyNav";
import InfoUtili from "@/components/InfoUtili";
import RSVPForm from "@/components/RSVPForm";
import Footer from "@/components/Footer";
import EditableText from "@/components/EditableText";
import EditableImage from "@/components/EditableImage";

// Hero images - different for mobile and desktop
const HERO_IMAGE_DESKTOP = "https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/xvccd006_IMG_0380_reduced.jpeg";
const HERO_IMAGE_MOBILE = "https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/z4u33r8q_breve-hero-mobile.jpg";

// Timeline specifica per BREVE - solo 3 momenti
const BREVE_TIMELINE = [
  { 
    ora: "16:00", 
    titolo: "Cerimonia", 
    desc: "Le sedie buone vanno a ruba per il momento serio (o quasi). Anche se la sposa non garantisce puntualità, conviene arrivare per tempo. Mamma, prepara i fazzoletti!",
    icon: "Heart"
  },
  { 
    ora: "18:00", 
    titolo: "Ricevimento", 
    desc: "Ci spostiamo alla Fortezza, una fortezza medioevale sul mare con tanto di galere per gli ospiti più tranquilli. Sì esatto, ospiti tranquilli non ne vogliamo! E' il momento di dare sfogo alla fame e alla sete più becera",
    icon: "Wine"
  },
  { 
    ora: "20:00", 
    titolo: "Cena", 
    desc: "Finalmente ci sediamo a tavola, già pieni e con il grande rimpianto di non aver comprato il vestito di (almeno) una taglia in più. Ma tanto che ce frega, abbiamo tutto domani per digiunare",
    icon: "UtensilsCrossed"
  },
];

const iconMap = { Heart, Wine, UtensilsCrossed };

// Photo gallery - 12 photos total (4 rows of 3)
const GALLERY_PHOTOS = [
  { src: "https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/fak9m80n_IMG_2032_reduced.jpg", id: "brief-photo-1" },
  { src: "https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/aeoxpbnq_IMG_0512_reduced.jpg", id: "brief-photo-2" },
  { src: "https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/tojjnny8_1.jpg", id: "brief-photo-3" },
  { src: "https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/bf6re3zm_2.jpg", id: "brief-photo-4" },
  { src: "https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/wkzd99or_3.jpg", id: "brief-photo-5" },
  { src: "https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/mhmxf15y_4.jpg", id: "brief-photo-6" },
  { src: "https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/wg8176wf_5.jpg", id: "brief-photo-7" },
  { src: "https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/tc9thxl0_6.jpg", id: "brief-photo-8" },
  { src: "https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/hdfv8w82_10.jpeg", id: "brief-photo-9" },
  { src: "https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/lb2s30db_11.jpeg", id: "brief-photo-10" },
  { src: "https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/z7vr2y65_12.jpeg", id: "brief-photo-11" },
  { src: "https://customer-assets.emergentagent.com/job_cfe525a3-480d-494a-826d-613b0026d712/artifacts/xt4lfab3_9.jpg", id: "brief-photo-12" },
];

const infoCards = [
  { icon: MapPin, title: "Dove", text: "Borgio Verezzi & Finale Ligure", sub: "Borgio nel cuore, Verezzi per la cerimonia e Finale Ligure per il ricevimento. Potrai finalmente dire anche tu \"Ah, la mia Liguria!\"" },
  { icon: CalendarCheck, title: "RSVP entro", text: WEDDING.deadline_rsvp, sub: "Lo sappiamo, tempi stretti e poco preavviso. Se pero' volete risponderci prima del 5 luglio siamo felicioni!" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }),
};

export default function BriefFlow() {
  // Set page title
  useEffect(() => {
    document.title = "Lelia & Stefano sposi in breve";
  }, []);

  return (
    <div className="bg-w-cream min-h-screen" data-testid="brief-flow">
      <StickyNav />

      {/* Hero Banner Image with Quote - Full Width */}
      <div className="w-full h-screen relative overflow-hidden">
        {/* Desktop image */}
        <img 
          src={HERO_IMAGE_DESKTOP}
          alt="Hero Banner Breve" 
          className="hidden md:block w-full h-full object-cover object-center"
        />
        {/* Mobile image */}
        <img 
          src={HERO_IMAGE_MOBILE}
          alt="Hero Banner Breve" 
          className="md:hidden w-full h-full object-cover object-center"
        />
        
        {/* Quote overlay at bottom - centered */}
        <div className="absolute bottom-0 left-0 right-0">
          {/* Dark gradient for text readability */}
          <div className="absolute inset-x-0 bottom-0 h-[250px] bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
          
          {/* Quote text - centered on desktop, left-aligned on mobile */}
          <div className="relative z-10 px-6 md:px-12 pb-10 md:pb-14 md:text-center text-left">
            <p className="font-accent italic text-white text-lg md:text-xl leading-relaxed drop-shadow-lg max-w-xl md:mx-auto">
              "perché vedi, Io credo che l'amore,<br />
              è l'amore che ci salverà"
            </p>
            <p className="font-body text-white/70 text-sm md:text-base mt-2 drop-shadow-md">
              Lucio Dalla - Henna
            </p>
          </div>
        </div>
      </div>

      {/* Hero Executive Summary */}
      <section id="home" className="section-padding pt-12 md:pt-16 max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-6"
        >
          <p className="font-body text-xs tracking-[0.3em] uppercase text-w-sage">Le cose importanti</p>
          <EditableText id="brief-title" tag="h1" className="font-heading text-4xl sm:text-5xl lg:text-6xl text-w-charcoal mt-2 leading-tight">
            Tutto quello che<br />dovete sapere.
          </EditableText>
          <EditableText id="brief-subtitle" tag="p" className="font-body text-w-muted mt-4 max-w-lg text-base md:text-lg leading-relaxed">
            {WEDDING.sposa} e {WEDDING.sposo} si sposano. {WEDDING.giorno} {WEDDING.data}.
            Borgio Verezzi. Ecco il riassunto per chi va di fretta.
          </EditableText>
        </motion.div>

        {/* Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10">
          {infoCards.map((card, i) => (
            <motion.div
              key={card.title}
              custom={i}
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              className="border border-w-sand bg-white/50 p-5 md:p-6 group hover:border-w-sage transition-colors duration-300"
              data-testid={`info-card-${i}`}
            >
              <card.icon className="w-5 h-5 text-w-sage mb-3" strokeWidth={1.5} />
              <h3 className="font-heading text-lg text-w-charcoal">{card.title}</h3>
              <p className="font-body text-sm text-w-charcoal font-medium mt-1">{card.text}</p>
              <p className="font-body text-xs text-w-muted mt-0.5 leading-relaxed">{card.sub}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Photo Gallery - 9 photos in 3 rows of 3 */}
      <section className="pt-4 pb-0 px-6 md:px-10">
        <div className="max-w-6xl mx-auto">
          {/* First row - 3 photos */}
          <div className="grid grid-cols-3 gap-3 md:gap-4 mb-3 md:mb-4">
            {GALLERY_PHOTOS.slice(0, 3).map((photo, i) => (
              <motion.div
                key={photo.id}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
                className="aspect-square overflow-hidden"
              >
                <EditableImage
                  id={photo.id}
                  defaultSrc={photo.src}
                  alt={`Foto ${i + 1}`}
                  className="w-full h-full"
                  imgClassName="hover:scale-105 transition-transform duration-700"
                />
              </motion.div>
            ))}
          </div>
          {/* Second row - 3 photos */}
          <div className="grid grid-cols-3 gap-3 md:gap-4 mb-3 md:mb-4">
            {GALLERY_PHOTOS.slice(3, 6).map((photo, i) => (
              <motion.div
                key={photo.id}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: (i + 3) * 0.1, duration: 0.6 }}
                className="aspect-square overflow-hidden"
              >
                <EditableImage
                  id={photo.id}
                  defaultSrc={photo.src}
                  alt={`Foto ${i + 4}`}
                  className="w-full h-full"
                  imgClassName="hover:scale-105 transition-transform duration-700"
                />
              </motion.div>
            ))}
          </div>
          {/* Third row - 3 photos */}
          <div className="grid grid-cols-3 gap-3 md:gap-4 mb-3 md:mb-4">
            {GALLERY_PHOTOS.slice(6, 9).map((photo, i) => (
              <motion.div
                key={photo.id}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: (i + 6) * 0.1, duration: 0.6 }}
                className="aspect-square overflow-hidden"
              >
                <EditableImage
                  id={photo.id}
                  defaultSrc={photo.src}
                  alt={`Foto ${i + 7}`}
                  className="w-full h-full"
                  imgClassName="hover:scale-105 transition-transform duration-700"
                />
              </motion.div>
            ))}
          </div>
          {/* Fourth row - 3 photos (10, 11, 12) */}
          <div className="grid grid-cols-3 gap-3 md:gap-4">
            {GALLERY_PHOTOS.slice(9, 12).map((photo, i) => (
              <motion.div
                key={photo.id}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: (i + 9) * 0.1, duration: 0.6 }}
                className="aspect-square overflow-hidden"
              >
                <EditableImage
                  id={photo.id}
                  defaultSrc={photo.src}
                  alt={`Foto ${i + 10}`}
                  className="w-full h-full"
                  imgClassName="hover:scale-105 transition-transform duration-700"
                />
              </motion.div>
            ))}
          </div>
          <div className="h-4" />
        </div>
      </section>

      {/* Timeline BREVE - Same style as STORIA */}
      <section id="giorno" className="section-padding bg-w-sand/30" data-testid="breve-timeline-section">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <p className="font-body text-xs tracking-[0.3em] uppercase text-w-sage">Il programma</p>
            <EditableText id="breve-timeline-title" tag="h2" className="font-heading text-4xl sm:text-5xl text-w-charcoal mt-2">
              Il Grande Giorno
            </EditableText>
            <EditableText id="breve-timeline-subtitle" tag="p" className="font-body text-w-muted mt-3 text-sm md:text-base max-w-md">
              Venerdi' 18 Settembre 2026. Un giorno che non dimenticherete. Neanche noi, speriamo.
            </EditableText>
          </motion.div>

          {/* Timeline with alternating layout like STORIA */}
          <div className="relative">
            {/* Vertical line */}
            <div className="timeline-line" />

            {BREVE_TIMELINE.map((event, i) => {
              const Icon = iconMap[event.icon] || Heart;
              const isLeft = i % 2 === 0;

              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: isLeft ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1, duration: 0.5 }}
                  className={`relative flex items-start mb-12 md:mb-16 ${
                    isLeft ? 'md:flex-row' : 'md:flex-row-reverse'
                  } flex-row`}
                  data-testid={`breve-timeline-event-${i}`}
                >
                  {/* Dot */}
                  <div className="absolute left-[24px] md:left-1/2 w-3 h-3 bg-w-sage rounded-full -translate-x-1/2 mt-2 z-10 ring-4 ring-w-cream" />

                  {/* Content */}
                  <div className={`ml-12 md:ml-0 md:w-[45%] ${
                    isLeft ? 'md:pr-12 md:text-right' : 'md:pl-12 md:text-left'
                  }`}>
                    <div className={`inline-flex items-center gap-2 mb-2 ${isLeft ? 'md:flex-row-reverse' : ''}`}>
                      <Icon className="w-4 h-4 text-w-sage" strokeWidth={1.5} />
                      <span className="font-mono-twist text-lg md:text-xl text-w-charcoal font-bold tracking-wider">{event.ora}</span>
                    </div>
                    <h3 className="font-heading text-xl md:text-2xl text-w-charcoal">{event.titolo}</h3>
                    <p className="font-body text-sm text-w-muted mt-1">{event.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Spacer before Info Utili */}
      <div className="h-[20px] bg-w-cream" />

      <InfoUtili />
      <RSVPForm />
      <Footer />
    </div>
  );
}
